import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import useApi from '../../hooks/useApi';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';

const generateTenantCodeFromName = (name) =>
  name
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const ClientForm = ({ open, onClose, client = null, onSubmitSuccess }) => {
  const api = useApi();
  const isEditMode = !!client;
  const [formData, setFormData] = useState({
    tenantCode: '',
    tenantName: '',
    tenantType: 'Client',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    if (client) {
      setFormData({
        tenantCode: client.TenantCode || '',
        tenantName: client.TenantName || '',
        tenantType: client.TenantType || 'Client',
      });
    } else {
      setFormData({
        tenantCode: '',
        tenantName: '',
        tenantType: 'Client',
      });
    }
    setError(null);
    setFormErrors({});
  }, [client, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (!isEditMode && field === 'tenantName') {
        next.tenantCode = generateTenantCodeFromName(value);
      }
      return next;
    });
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
    if (!isEditMode && field === 'tenantName' && formErrors.tenantCode) {
      setFormErrors((prev) => ({
        ...prev,
        tenantCode: undefined,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!isEditMode && !formData.tenantCode.trim()) {
      errors.tenantCode = 'Client code is required';
    } else if (!isEditMode && !/^[A-Z0-9_]+$/.test(formData.tenantCode.trim().toUpperCase())) {
      errors.tenantCode = 'Client code must contain only uppercase letters, numbers, and underscores';
    }
    
    if (!formData.tenantName.trim()) {
      errors.tenantName = 'Client name is required';
    } else if (formData.tenantName.trim().length < 2) {
      errors.tenantName = 'Client name must be at least 2 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        const response = await api.put(`/tenants/${client.TenantId}`, {
          tenantName: formData.tenantName.trim(),
          isActive: client.IsActive !== undefined ? client.IsActive : true,
        });

        if (response.data?.success) {
          onSubmitSuccess?.();
        } else {
          setError(getApiErrorMessage(null, 'Failed to update client'));
        }
      } else {
        const response = await api.post('/tenants', {
          tenantCode: formData.tenantCode.trim().toUpperCase(),
          tenantName: formData.tenantName.trim(),
          tenantType: formData.tenantType,
        });

        if (response.data?.success) {
          onSubmitSuccess?.();
        } else {
          setError(getApiErrorMessage(null, 'Failed to create client'));
        }
      }
    } catch (err) {
      console.error('Error saving client:', err);
      setError(getApiErrorMessage(err, `Failed to ${isEditMode ? 'update' : 'create'} client`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEditMode ? 'Edit Client' : 'Add New Client'}
      </DialogTitle>
      <DialogContent dividers>
        
        <TextField
          autoFocus
          margin="dense"
          id="tenantName"
          label="Client Name"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.tenantName}
          onChange={handleChange('tenantName')}
          error={!!formErrors.tenantName}
          helperText={formErrors.tenantName}
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          id="tenantCode"
          label="Client Code"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.tenantCode}
          error={!!formErrors.tenantCode}
          helperText={
            formErrors.tenantCode ||
            (!isEditMode ? 'Generated automatically from client name' : undefined)
          }
          disabled={isEditMode}
          InputProps={{
            readOnly: !isEditMode,
          }}
          inputProps={{
            style: { textTransform: 'uppercase' },
          }}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#000000',
            '&:hover': {
              backgroundColor: '#333333',
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientForm;
